<template>
  <div class="kyc-document-capture">
    <f7-card class="capture-card" :padding="false">
      <f7-card-content class="capture-card-content" :padding="false">
        <div class="capture-header">
          <span class="capture-type-badge">{{ documentTypeLabel }}</span>
          <span v-if="documentType.description" class="capture-desc">{{ documentType.description }}</span>
        </div>

        <!-- Hidden file inputs -->
        <input
          ref="cameraInput"
          type="file"
          accept="image/*;capture=environment"
          class="hidden-input"
          @change="onFileSelected"
        />
        <input
          ref="fileInput"
          type="file"
          accept="image/jpeg,image/png,application/pdf"
          class="hidden-input"
          @change="onFileSelected"
        />

        <!-- Upload prompt (idle state) -->
        <f7-link
          v-if="!previewUrl && !uploaded && !error"
          class="capture-zone"
          @click="showPicker"
          role="button"
          tabindex="0"
          @keydown.enter="showPicker"
          @keydown.space.prevent="showPicker"
        >
          <f7-icon f7="camera_fill" :size="40" color="#8e8e93" />
          <p class="capture-prompt">{{ $t('kycDocument.tapToCapture') }}</p>
          <p class="capture-hint">{{ $t('kycDocument.tapToCaptureHint') }}</p>
        </f7-link>

        <!-- Upload progress bar -->
        <div v-if="uploading" class="capture-progress">
          <f7-progressbar :progress="uploadProgress" />
          <p class="progress-text">{{ $t('kycDocument.uploading') }}</p>
        </div>

        <!-- Error state -->
        <div v-if="error && !uploading && !uploaded" class="capture-error">
          <f7-icon f7="exclamationmark_triangle_fill" :size="28" color="red" />
          <p>{{ error }}</p>
          <f7-button small round raised fill @click="retake">{{ $t('kycDocument.retry') }}</f7-button>
        </div>

        <!-- Preview -->
        <div v-if="previewUrl && !uploaded && !uploading" class="capture-preview">
          <img
            v-if="currentFile?.type?.startsWith('image/')"
            :src="previewUrl"
            class="preview-image"
            :alt="$t('common.a11y.documentPreview')"
          />
          <div v-else class="preview-pdf">
            <f7-icon f7="doc_text_fill" :size="48" color="#8e8e93" />
            <span>{{ currentFile?.name }}</span>
          </div>
          <div class="preview-actions">
            <f7-button small  round raised fill @click="retake">{{ $t('kycDocument.retake') }}</f7-button>
            <f7-button small  round raised @click="useFile">{{ $t('kycDocument.useThisDocument') }}</f7-button>
          </div>
        </div>

        <!-- Uploaded state -->
        <div v-if="uploaded" class="capture-uploaded">
          <f7-icon f7="checkmark_circle_fill" :size="36" color="green" />
          <span>{{ $t('kycDocument.uploaded') }}</span>
        </div>
      </f7-card-content>
    </f7-card>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useKycDocumentUpload } from '../../js/composables/useKycDocumentUpload.js'
import { Capacitor } from '@capacitor/core'
import i18n from '../../js/i18n.js'
const { t } = i18n.global
const props = defineProps({
  groupId: { type: String, required: true },
  documentType: {
    type: Object,
    required: true,
    validator: (v) => v && v.type && v.label,
  },
  issuingCountry: { type: String, default: '' },
})

const emit = defineEmits(['uploaded'])

const {
  uploading,
  uploadError,
  uploadFile: doUploadFile,
  reset: resetUpload,
} = useKycDocumentUpload()

const cameraInput = ref(null)
const fileInput = ref(null)
const previewUrl = ref(null)
const currentFile = ref(null)
const documentId = ref(null)
const uploadedDocType = ref(null)
const error = ref(null)
const uploadProgress = ref(0)

const capturedDocs = ref([])

const documentTypeLabel = computed(() => props.documentType?.label || props.documentType?.type || '')

function showPicker() {
  // Offer camera on mobile (rear camera) and file picker fallback
  if (Capacitor.isNativePlatform() || /Android|iPhone|iPad/i.test(navigator.userAgent)) {
    cameraInput.value?.click()
  } else {
    fileInput.value?.click()
  }
}

function onFileSelected(e) {
  const file = e.target.files?.[0]
  if (!file) return
  const oldUrl = previewUrl.value
  previewUrl.value = null
  currentFile.value = file
  if (oldUrl) URL.revokeObjectURL(oldUrl)
  previewUrl.value = URL.createObjectURL(file)
  error.value = null
  e.target.value = ''
}

function retake() {
  resetState()
}

function resetState() {
  if (previewUrl.value) URL.revokeObjectURL(previewUrl.value)
  previewUrl.value = null
  currentFile.value = null
  error.value = null
  documentId.value = null
  uploadProgress.value = 0
  resetUpload()
}

async function useFile() {
  const file = currentFile.value
  if (!file) return
  error.value = null

  // Validate file type client-side
  const allowed = [
    'image/jpeg', 'image/jpg', 'image/png', 'application/pdf',
  ]
  if (!allowed.includes(file.type)) {
    error.value = t('kycDocument.unsupportedFormat')
    return
  }
  if (file.size > 10 * 1024 * 1024) {
    error.value = t('kycDocument.fileTooLarge')
    return
  }

  try {
    const docResp = await doUploadFile({
      groupId: props.groupId,
      documentType: props.documentType.type,
      issuingCountry: props.issuingCountry || undefined,
      file,
      frontSide: !!props.documentType.frontSide,
    })

    documentId.value = docResp.id
    uploaded.value = true
    uploadedDocType.value = props.documentType.type
    capturedDocs.value = [...capturedDocs.value, docResp]
    emit('uploaded', docResp)
  } catch (err) {
    error.value = t('kycDocument.uploadFailed')
  }
}

const uploaded = computed(() => !!documentId.value)
</script>

<style scoped>
.kyc-document-capture {
  margin: 12px 0;
}
.capture-card {
  margin: 0;
  box-shadow: none;
}
.capture-card-content {
  padding: 16px;
}
.capture-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
  gap: 8px;
}
.capture-type-badge {
  font-weight: 600;
  font-size: 14px;
  color: var(--f7-theme-color, #0009ff);
  white-space: nowrap;
}
.capture-desc {
  font-size: 12px;
  color: var(--f7-on-surface-variant, #8e8e93);
  line-height: 1.4;
}
.hidden-input {
  display: none;
}
.capture-zone {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 160px;
  border: 2px dashed var(--f7-list-item-border-color, #ccc);
  border-radius: 12px;
  gap: 8px;
  transition: border-color 0.2s;
  width: 100%;
  padding: 24px;
  text-align: center;
}
.capture-zone:active {
  border-color: var(--f7-theme-color, #0009ff);
}
.capture-prompt {
  font-size: 14px;
  font-weight: 500;
  color: var(--f7-text-color, #333);
  margin: 0;
}
.capture-hint {
  font-size: 12px;
  color: var(--f7-on-surface-variant, #8e8e93);
  margin: 0;
}
.capture-progress {
  text-align: center;
  padding: 12px 0;
}
.capture-progress :deep(.f7-progressbar) {
  --f7-progressbar-height: 6px;
  --f7-progressbar-bg-color: var(--f7-list-item-border-color, #e5e5e5);
  --f7-progressbar-color: var(--f7-theme-color, #0009ff);
}
.progress-text {
  font-size: 13px;
  color: var(--f7-on-surface-variant, #8e8e93);
  margin: 8px 0 0;
}
.capture-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 16px 0;
}
.capture-error p {
  margin: 0;
  font-size: 13px;
  color: var(--f7-on-surface-variant, #8e8e93);
}
.capture-uploaded {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 0;
  color: #34c759;
  font-weight: 500;
}
.capture-preview {
  text-align: center;
}
.preview-image {
  max-width: 100%;
  max-height: 220px;
  object-fit: contain;
  border-radius: 8px;
  border: 1px solid var(--f7-list-item-border-color, #e5e5e5);
}
.preview-pdf {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 20px;
  background: var(--f7-block-bg-color, #f7f7f7);
  border-radius: 8px;
}
.preview-pdf span {
  font-size: 13px;
  color: var(--f7-text-color, #333);
}
.preview-actions {
  display: flex;
  gap: 8px;
  justify-content: center;
  margin-top: 12px;
}
</style>